import React, { Fragment, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import CSSModules from 'react-css-modules';
import get from 'lodash/get';

import Button from '../../../../Button';
import TransactionInfo from '../../TransactionInfo';
import VideoVisitBtn from '../../VideoVisitBtn';
import { formatPaymentInfo } from '../../../utils/appointment';
import { useToggleFlag } from '../../../../../hocs/useToogleFlag';
import ActionConfirmation from '../../../../common/ActionConfirmation';
import {
  INLINE_TYPES,
  LAB_TYPE_CODE,
  PAYMENT_STATUSES,
  IMAGING_TYPE_CODE,
  TECH_CONNECT_CODE,
  APPOINTMENT_STATUSES,
  URGENT_CARE_TYPE_CODE,
  OFFICE_VISIT_TYPE_CODE,
  URGENT_VISIT_TYPE_CODE,
  VIRTUAL_VISIT_TYPE_CODE,
  HOLD_MY_SPOT_VISIT_TYPE_CODE,
  TELEMEDICINE_VISIT_TYPE_CODE,
} from '../../../../../constants';

import styles from './styles.styl';

const { DRAFT, WAITING, PENDING, UPCOMING, CHECKED_IN } = APPOINTMENT_STATUSES;

function AppointmentFooter({
  providerContext,
  renderTestButton,
  appointment,
  formatMessage,
  handleVideoVisit,
  onPayNow,
  onCancel,
  disabled,
  isPendingStatus,
}) {
  const [opened, openConfirm, closeConfirm] = useToggleFlag();
  const { cancel } = get(appointment, 'allowedActions') || {};
  const copay = get(appointment, 'copay');
  const code = get(appointment, 'visitType.code');
  const status = get(appointment, 'status.coreAppName');
  const paymentStatus = get(appointment, 'paymentStatus');
  const prohibitCancelationForPatient = get(
    appointment,
    'reasonType.prohibitCancelationForPatient',
    false,
  );

  const { id, scope, canPay } = formatPaymentInfo(appointment, true);

  const isZeroCopay = copay === 0;
  const isPaid = paymentStatus === PAYMENT_STATUSES.PAID;
  const isVoided = paymentStatus === PAYMENT_STATUSES.VOIDED;

  const handleCancel = () => {
    onCancel();
    closeConfirm();
  };

  const patientPaymentRow = () => (
    <Fragment>
      {id !== null && (
        <div>
          <span styleName="paymentTitle">
            {formatMessage({ id: 'appointment.form.costTitle' })}:
          </span>
          <span styleName="paymentValue">{formatMessage({ id }, scope)}</span>
          {(isPaid || (isVoided && Boolean(copay))) && (
            <TransactionInfo
              appointment={appointment}
              formatMessage={formatMessage}
            />
          )}
        </div>
      )}
      {canPay && !isPendingStatus && !isZeroCopay && (
        <Button
          onClick={onPayNow}
          color="primary"
          styleName="btn btn--wide"
          raised
        >
          {formatMessage({ id: 'appointment.actions.pay' })}
        </Button>
      )}
    </Fragment>
  );

  const providerContextPaymentRow = () => {
    const isVV = get(appointment, 'visitType.code') === VIRTUAL_VISIT_TYPE_CODE;

    const patientAllowedStart = get(appointment, 'allowedActions.patientStart');

    if (!isVV || isPendingStatus) {
      return null;
    }

    return (
      <div styleName="btn--wide btn--offset">
        {patientAllowedStart && !isPendingStatus ? (
          <VideoVisitBtn onStartVideoVisit={handleVideoVisit} type="primary" />
        ) : (
          renderTestButton()
        )}
      </div>
    );
  };

  const renderPaymentRow = () => {
    if (INLINE_TYPES.includes(code)) {
      return null;
    }

    return providerContext ? providerContextPaymentRow() : patientPaymentRow();
  };

  function renderActionButtons() {
    return (
      <Fragment>
        {prohibitCancelationForPatient ? (
          <div className={styles.contactProvider}>
            {formatMessage({ id: 'appointment.actions.askProviderForCancel' })}
          </div>
        ) : (
          <Button
            onClick={openConfirm}
            color="danger-white"
            disabled={disabled}
            raised
            styleName="btn--wide btn--offset"
          >
            {formatMessage({ id: 'appointment.actions.cancelVisit' })}
          </Button>
        )}
        <ActionConfirmation
          opened={opened}
          width="rubber"
          onClose={closeConfirm}
          onSubmit={handleCancel}
          title={formatMessage({
            id: isPaid
              ? 'appointment.actions.cancelUrgentVisitConfirmation'
              : 'appointment.actions.cancelVisitConfirmation',
          })}
          actionButtonText={formatMessage({
            id: 'appointment.actions.yesCancel',
          })}
          cancelButtonText={{ id: 'appointment.actions.noGoBack' }}
        />
      </Fragment>
    );
  }

  const allowedStatuses = useMemo(() => {
    switch (code) {
      case LAB_TYPE_CODE:
      case IMAGING_TYPE_CODE:
      case TECH_CONNECT_CODE:
      case URGENT_CARE_TYPE_CODE:
        return [UPCOMING, CHECKED_IN];

      case URGENT_VISIT_TYPE_CODE:
        return [DRAFT, WAITING];

      case VIRTUAL_VISIT_TYPE_CODE:
        return [UPCOMING, PENDING];

      case OFFICE_VISIT_TYPE_CODE:
      case HOLD_MY_SPOT_VISIT_TYPE_CODE:
      case TELEMEDICINE_VISIT_TYPE_CODE:
        return cancel ? [UPCOMING, PENDING, CHECKED_IN] : [];

      default:
        return [];
    }
  }, [code, cancel]);

  return (
    <div styleName="footer">
      <div styleName="confirmationRow">
        {allowedStatuses.includes(status) && renderActionButtons()}
      </div>
      <div styleName="paymentRow">{renderPaymentRow()}</div>
    </div>
  );
}

AppointmentFooter.propTypes = {
  appointment: PropTypes.object,
  formatMessage: PropTypes.func,
  handleVideoVisit: PropTypes.func,
  disabled: PropTypes.bool,
  providerContext: PropTypes.bool,
  onPayNow: PropTypes.func,
  onCancel: PropTypes.func,
  renderTestButton: PropTypes.func,
  isPendingStatus: PropTypes.bool,
};

export default CSSModules(AppointmentFooter, styles, { allowMultiple: true });



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/AppointmentDetailsPopup/AppointmentFooter/index.jsx